import { IGeoTags } from 'shared/types/tags';
import { getGeoValueByType, getTermsValue, setGeo, TGeoValue } from '../../../JsonQuery';
import { TAdornment, TTagsManager } from '../types/tag';
import { createTagValue } from './createTagValue';

interface IParams<T extends TGeoValue> {
  jsonQueryKey: TGeoValue['type'];
  id: string;
  labelGetter: (params: TLabelGetterParams<T>) => string;
  getLeftAdornment?: (geoTags: TLabelGetterParams<T>) => TAdornment | undefined;
  getRightAdornment?: (geoTags: TLabelGetterParams<T>) => TAdornment | undefined;
}

type TLabelGetterParams<T extends TGeoValue> = {
  geoTags: IGeoTags;
  geoValue: T;
  regionId?: number;
};

export const createGeoTagsManager = <T extends TGeoValue>({
  jsonQueryKey,
  id,
  labelGetter,
  getLeftAdornment,
  getRightAdornment,
}: IParams<T>): TTagsManager => ({
  id,
  extractTagsFromJsonQuery: ({ jsonQuery, geoTags }) => {
    const geoValue = getGeoValueByType<T>(jsonQueryKey)(jsonQuery);

    if (geoValue === null) {
      return null;
    }

    const regionId = getTermsValue('region')(jsonQuery)?.[0];

    return geoValue.map(geo => {
      const geoParams = { geoTags, geoValue: geo, regionId };

      return {
        label: labelGetter(geoParams),
        value: createTagValue(id, 'id' in geo ? geo.id.toString() : geo.name),
        leftAdornment: getLeftAdornment?.(geoParams),
        rightAdornment: getRightAdornment?.(geoParams),
      };
    });
  },
  modifyJsonQueryByRemovingTag: ({ jsonQuery, removingTagValue }) => {
    const values = getGeoValueByType<T>(jsonQueryKey)(jsonQuery);

    if (!values) {
      return jsonQuery;
    }

    const newValues = values.filter(geo => {
      const tagValue = 'id' in geo ? geo.id.toString() : geo.name;

      return tagValue !== removingTagValue;
    });

    return setGeo()(jsonQuery)(newValues);
  },
});
